import type { FC } from "react";
import { Fragment, useRef, useState } from "react";

interface AmountSectionProps {
	basicDonationAmount: number;
	onAmountChange: (amount: number) => void;
	isVisible: boolean;
	recurring: boolean;
}

const MONTHLY_PRESETS = [200, 300, 500, 1000];
const ONE_OFF_PRESETS = [500, 1000, 2000, 5000];

export const AmountSection: FC<AmountSectionProps> = ({
	onAmountChange,
	isVisible,
	recurring,
}) => {
	const [customAmount, setCustomAmount] = useState("");
	const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
	const [showValidation, setShowValidation] = useState(false);
	const prevRecurringRef = useRef(recurring);

	const presets = recurring ? MONTHLY_PRESETS : ONE_OFF_PRESETS;

	// Clear selection when switching between recurring/one-off
	if (prevRecurringRef.current !== recurring) {
		prevRecurringRef.current = recurring;
		if (selectedPreset !== null && !presets.includes(selectedPreset)) {
			setSelectedPreset(null);
			onAmountChange(0);
		}
	}

	if (!isVisible) return null;

	const handlePresetAmountChange = (amount: number) => {
		setSelectedPreset(amount);
		setCustomAmount("");
		setShowValidation(false);
		onAmountChange(amount);
	};

	const handleCustomAmountChange = (value: string) => {
		setCustomAmount(value);
		setSelectedPreset(null);
		const numericValue = parseFloat(value) || 0;
		onAmountChange(numericValue);
	};

	const handleCustomAmountFocus = () => {
		setShowValidation(true);
		setSelectedPreset(null);
	};

	const handleCustomAmountBlur = () => {
		if (customAmount === "") {
			setShowValidation(false);
		}
	};

	return (
		<section id="amount-section" className="amount-section">
			<h2 className="text-lg mt-8">I would like to gift</h2>
			<div
				className="radio-group flex gap-2"
				key={recurring ? "monthly" : "one-off"}
			>
				{presets.map((amount) => (
					<Fragment key={amount}>
						<input
							type="radio"
							id={`amount-section--donate-${amount}`}
							name="donation-amount"
							value={amount}
							checked={selectedPreset === amount}
							onChange={() => handlePresetAmountChange(amount)}
						/>
						<label
							htmlFor={`amount-section--donate-${amount}`}
							className="rounded-full text-brand larger-button"
						>
							{amount} HKD
						</label>
					</Fragment>
				))}
			</div>

			<div className={`flex flex-col mb-2 ${showValidation ? "validate" : ""}`}>
				<label className="inline-block mb-2 mt-4" htmlFor="custom-amount-input">
					Or a custom amount:
				</label>
				<span className="custom-amount-input-wrapper border rounded-full focus-outline">
					<input
						type="number"
						inputMode="numeric"
						min="0"
						max="999999.99"
						step="0.01"
						id="custom-amount-input"
						name="custom-amount-input"
						className="custom-amount-input inline-block text-md larger-input"
						value={customAmount}
						onChange={(e) => handleCustomAmountChange(e.target.value)}
						onFocus={handleCustomAmountFocus}
						onBlur={handleCustomAmountBlur}
						placeholder="Enter your custom amount here"
					/>
					<span className="currency-suffix">HKD</span>
				</span>
			</div>
		</section>
	);
};
